// PDF preview using PDF.js — renders pages to a canvas inline, works
// in every browser regardless of plugin support, with pagination + zoom.
// Source URL must be same-origin (we feed it `/api/files/proxy`).
import { useEffect, useRef, useState, useCallback } from "react";
import * as pdfjsLib from "pdfjs-dist";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, AlertCircle,
} from "lucide-react";

// The worker is served from /public/pdf.worker.min.js — see MarkerReviewModal.
// A single local worker keeps every pdf.js consumer on the same version and
// avoids the unpkg CDN + .mjs vs .js resolution issues we hit with v3.
pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

export default function PdfJsViewer({ url }) {
  const containerRef = useRef(null);
  const [pdf, setPdf] = useState(null);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1.2);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // Load the document once per url
  useEffect(() => {
    let cancelled = false;
    setLoading(true); setErr(""); setPdf(null); setPage(1);
    const task = pdfjsLib.getDocument({ url, withCredentials: true });
    task.promise
      .then((doc) => { if (!cancelled) setPdf(doc); })
      .catch((e) => { if (!cancelled) setErr(e?.message || "Failed to load PDF"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; task.destroy?.(); };
  }, [url]);

  const renderPage = useCallback(async () => {
    if (!pdf || !containerRef.current) return;
    const p = await pdf.getPage(page);
    const viewport = p.getViewport({ scale: zoom });
    const canvas = document.createElement("canvas");
    canvas.width = viewport.width;
    canvas.height = viewport.height;
    canvas.className = "block mx-auto bg-white shadow-md";
    const ctx = canvas.getContext("2d");
    await p.render({ canvasContext: ctx, viewport }).promise;
    if (containerRef.current) {
      // Clear via DOM API (no innerHTML) so we satisfy the strict "no raw
      // HTML assignment" rule the code review enforces. Functionally
      // identical to ``innerHTML = ""``.
      while (containerRef.current.firstChild) {
        containerRef.current.removeChild(containerRef.current.firstChild);
      }
      containerRef.current.appendChild(canvas);
    }
  }, [pdf, page, zoom]);

  useEffect(() => { renderPage(); }, [renderPage]);

  if (loading) {
    return (
      <div className="flex items-center justify-center w-full h-[70vh]">
        <Loader2 className="w-7 h-7 animate-spin text-stone-400" />
      </div>
    );
  }
  if (err) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-700">
        <AlertCircle className="w-4 h-4" /> {err}
      </div>
    );
  }
  if (!pdf) return null;

  return (
    <div className="w-full flex flex-col items-center gap-3 py-2">
      <div className="flex items-center gap-2 sticky top-0 z-10 bg-white/85 backdrop-blur-sm border border-stone-200 rounded-full px-3 py-1.5 text-xs">
        <button data-testid="pdf-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 disabled:opacity-30">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="tabular-nums font-bold text-stone-800" data-testid="pdf-page-num">{page} / {pdf.numPages}</span>
        <button data-testid="pdf-next" disabled={page >= pdf.numPages} onClick={() => setPage((p) => Math.min(pdf.numPages, p + 1))}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100 disabled:opacity-30">
          <ChevronRight className="w-4 h-4" />
        </button>
        <span className="w-px h-4 bg-stone-300 mx-1" />
        <button data-testid="pdf-zoomout" onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.2).toFixed(2)))}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100">
          <ZoomOut className="w-3.5 h-3.5" />
        </button>
        <span className="tabular-nums w-12 text-center text-stone-700">{Math.round(zoom * 100)}%</span>
        <button data-testid="pdf-zoomin" onClick={() => setZoom((z) => Math.min(3, +(z + 0.2).toFixed(2)))}
          className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-stone-100">
          <ZoomIn className="w-3.5 h-3.5" />
        </button>
      </div>
      <div ref={containerRef} className="w-full overflow-auto" data-testid="pdf-canvas-host" />
    </div>
  );
}
